import api from "@/services/api"
import type { Category } from "../sharedTypes/categories"
import { useQuery } from "@tanstack/vue-query"

import type { MaybeRef } from "@vueuse/core"

import type { Breadcrumb } from "@/composables/useBreadcrumbs"
import { queryClient } from "@/queryClient"
import { BASE_CATEGORIES_QUERY_KEY } from "@/services/queryKeys"

export const listCategories = async (): Promise<
  Array<Category>
> => {
  const response = await api.get<Category[]>(
    "/products/categories/"
  )
  return response.body
}

export const useCategories = (
  parentCategoryId?: MaybeRef<string | undefined | null>
) => {
  const { data: allCategories, ...rest } = useQuery(
    [BASE_CATEGORIES_QUERY_KEY],
    () => listCategories(),
    {
      staleTime: 60 * 60 * 1000,
    }
  )

  const data = computed(() => {
    if (!allCategories.value) {
      return []
    } else {
      return unref(parentCategoryId) === undefined
        ? allCategories.value
        : allCategories.value?.filter(
            (category) =>
              category.parentCategoryId === parentCategoryId
          )
    }
  })

  const getBreadcrumbForCategoryId = (
    categoryId: MaybeRef<string | null>
  ): Breadcrumb | undefined => {
    const categoryId_ = unref(categoryId)
    if (categoryId_ === null) {
      return undefined
    } else {
      const self = allCategories.value?.find(
        ({ id }) => id === categoryId_
      )
      if (!self) return undefined

      return {
        to: {
          name: "fullTill",
          params: {
            parentCategoryId: self.id,
          },
        },
        label: self.name,
      }
    }
  }

  const getBreadcrumbs = (
    categoryId: MaybeRef<string | null>
  ) =>
    computed<Breadcrumb[]>((): Breadcrumb[] => {
      if (unref(categoryId) === null) return []
      const self = getById(categoryId)
      if (!self.value) {
        return []
      }
      const breadcrumb: Breadcrumb = {
        to: {
          name: "fullTill",
          params: {
            parentCategoryId: self.value.id,
          },
        },
        label: self.value.name,
      }

      return [
        ...self.value.path
          .map((parentCategoryId_) =>
            getBreadcrumbForCategoryId(parentCategoryId_)
          )
          .filter((value) => value !== undefined),
        breadcrumb,
      ]
    })

  const asOptions = computed(() => [
    {
      id: null,
      label: "---",
    },
    ...data.value.map(({ id, namePath }) => ({
      id: id,
      label: namePath,
    })),
  ])

  const getById = (id: MaybeRef<string | null>) =>
    computed(() => {
      if (unref(id) === null) return

      return allCategories.value?.find(
        (category) => category.id === id
      )
    })

  return {
    allCategories: readonly(allCategories),
    data: readonly(data),
    getBreadcrumbs,
    ...rest,
    asOptions,
    getById,
  }
}

export const prefetchCategories = async () => {
  await queryClient.prefetchQuery(
    [BASE_CATEGORIES_QUERY_KEY],
    listCategories,
    { staleTime: 60 * 60 * 1000 }
  )
}
